import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, ThemeToggle } from "@/components/AppShell";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset your password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex items-center justify-between p-4">
        <Link to="/"><Logo /></Link>
        <ThemeToggle />
      </div>
      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-6" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center" data-testid="reset-password-missing-token">
              <AlertTriangle className="mx-auto h-8 w-8 text-muted-foreground" />
              <h1 className="mt-3 font-display text-xl font-semibold">Invalid reset link</h1>
              <p className="mt-1 text-sm text-muted-foreground">Request a new password reset link and try again.</p>
              <Button asChild className="mt-6 w-full">
                <Link to="/forgot-password" data-testid="reset-password-request-new-link">Request new link</Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-xl font-semibold">Choose a new password</h1>
              <p className="mt-1 text-sm text-muted-foreground">Enter a new password for your account.</p>
              <form onSubmit={submit} className="mt-5 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="reset-password">New password</Label>
                  <Input id="reset-password" type="password" required minLength={8} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 8 characters" data-testid="reset-password-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-password-confirm">Confirm password</Label>
                  <Input id="reset-password-confirm" type="password" required minLength={8} value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    placeholder="Re-enter your password" data-testid="reset-password-confirm-input" />
                </div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="reset-password-submit-button">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
